import type { CSSProperties } from 'react';
import { WEDGE_SETTINGS_ACTION } from '@territory/tokens';
import { radius } from '../scale.js';
import { wedgeExactRole } from './wedgeRoles.js';

export type WedgeYouLook = 'you-upgrade-card' | 'you-join-request' | 'you-join-request-busy';
export const isWedgeYouLook = (look: string | undefined): look is WedgeYouLook => !!look && look.startsWith('you-');

/* Released You tab controls.
   `you-upgrade-card` is the free rep upgrade card: a full width, 16px radius flex row on the glass callout variables, with the
   inverted page ink as the fallback when the callout layer is absent. The shadow fallback is the fixed role `profileCtaShadow`. The caller
   draws the glyph tile, the two text lines and the arrow. The card is a native button that inherits the font of the page.
   `you-join-request` is the Request pill of the Join a team workspace form: a blue capsule with 9px by 14px padding and 10.5px bold
   mono type, uppercase, with 1px of tracking. `you-join-request-busy` is the same pill at 0.6 opacity while the request is in flight.
   None draws a transparent hit area (owner ruling SE-6a/SE-6b: legacy geometry). */
export function wedgeYouStyle(look: WedgeYouLook): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'you-upgrade-card') return {
    font: 'inherit', textAlign: 'left', width: '100%', boxSizing: 'border-box', margin: 0,
    background: `var(--rv-callout-bg, ${r('label')})`, color: `var(--rv-callout-ink, ${r('bg')})`,
    border: '1px solid var(--rv-callout-border, transparent)',
    backdropFilter: 'var(--rv-callout-blur, none)', WebkitBackdropFilter: 'var(--rv-callout-blur, none)',
    borderRadius: radius.card, padding: 18, display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer',
    boxShadow: `var(--rv-callout-shadow, ${WEDGE_SETTINGS_ACTION.profileCtaShadow})`,
  };
  return {
    font: 'inherit', fontFamily: r('mono'), fontSize: 10.5, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
    color: r('bg'), background: r('blue'), border: `1px solid ${r('blue')}`, borderRadius: radius.pill, padding: '9px 14px', cursor: 'pointer',
    ...(look === 'you-join-request-busy' ? { opacity: 0.6 } : {}),
  };
}
